//#region src/TaskQueue.ts
/**
* A task queue that processes named tasks sequentially with deduplication and throttling.
*
* Features:
* - **Sequential processing**: Only one task runs at a time
* - **Deduplication**: If the same task is enqueued within the throttle interval,
*   returns the existing promise instead of creating a new queue item
* - **Throttling**: Prevents rapid successive enqueues of the same task
*
* How it works:
* 1. Tasks are enqueued by name with a timestamp
* 2. If a task with the same name was enqueued recently (within interval),
*    the existing promise is returned
* 3. Otherwise, a new queue item is created and added to the queue
* 4. The dequeue process runs automatically, processing items one by one
* 5. Each task waits for the previous one to complete before starting
*/
var TaskQueue = class {
	items;
	pendingPromise;
	constructor(tasksConfig, interval = 0) {
		this.tasksConfig = tasksConfig;
		this.interval = interval;
		this.items = [];
		this.pendingPromise = false;
		this.interval = interval;
	}
	enqueue(taskName) {
		const now = Date.now();
		const lastEnqueue = this.items.findLast((item) => item.taskName === taskName);
		if (lastEnqueue && now - lastEnqueue.startTime < this.interval) return lastEnqueue.promise;
		const { promise, resolve, reject } = Promise.withResolvers();
		this.items.push({
			taskName,
			startTime: now,
			promise,
			resolve,
			reject
		});
		this.dequeue();
		return promise;
	}
	async dequeue() {
		if (this.pendingPromise) return false;
		const item = this.items.shift();
		if (!item) return false;
		this.pendingPromise = true;
		try {
			await this.tasksConfig[item.taskName]();
			item.resolve();
		} catch (e) {
			item.reject(e);
		}
		this.pendingPromise = false;
		this.dequeue();
		return true;
	}
	get size() {
		return this.items.length;
	}
};

//#endregion
export { TaskQueue };